/**
 * Unified User type definition
 * Unified User type consolidating all user data
 */

export interface User {
  // Core identification
  id: string;
  email: string;
  
  // Names
  firstName: string;
  lastName: string;
  fullName: string; // Generated field
  
  // Contact
  phone?: string; // Office phone
  mobilePhone?: string; // Mobile phone
  officeExtension?: string;
  
  // Roles & Status
  roles: string[];
  isActive: boolean;
  employmentStatus: 'pending' | 'active' | 'terminated';
  isLoanOfficer: boolean; // Generated from roles
  
  // Organization
  organizationId: string;
  organization?: {
    id: string;
    name: string;
  };
  branchName?: string;
  branchAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  addressPreference?: 'corporate' | 'custom';
  
  // Professional (loan officer specific)
  nmlsNumber?: string;
  stateLicenseNumber?: string;
  licensedStates?: string[];
  title?: string;
  yearsOfExperience?: number | null;
  mortgageIndustryStartYear?: number | null;
  bio?: string;
  specialties?: string[];
  mortgageSpecialties?: string[];
  
  // Photos & Media
  profilePhotoFacial?: string; // Headshot
  profilePhotoBody?: string; // Full body
  photoCropData?: Record<string, unknown>;
  
  // Website & Marketing
  website?: string;
  websiteTemplate?: string;
  websiteSlug?: string;
  websiteEnabled?: boolean;
  websiteSettings?: Record<string, unknown>;
  profilePublishedAt?: string;
  profileViewCount?: number;
  
  // Social Media
  linkedinUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  tiktokUrl?: string;
  calendarLink?: string;
  
  // Compensation
  currentCompensationPlan?: number;
  compensationLockedUntil?: string;
  commissionRate?: number;
  bonusStructure?: Record<string, unknown>;
  compensationTier?: string;
  brokerCommissionSplit?: number; // Broker split percentage for brokered loans
  mentorId?: string; // Assigned mentor user ID
  mentorshipActive?: boolean; // Whether mentorship is currently active
  brokerMentorCommissionSplit?: number; // % of broker commission allocated to mentor (0-50)
  brokeredDealsCompleted?: number; // Count of approved brokered deals (graduates at 5)
  // Existing-but-previously-unmapped (UserCompensation reads these from `users`; pricing reads bankedCompensation)
  bankedCompensation?: number; // LO banked comp percentage (e.g. 1.25 for 1.25%)
  maximumCompensation?: number; // Cap dollars
  bankedOverrideBps?: number; // bps allocated to an override recipient
  overrideRecipientId?: string; // user.id who receives the override

  // Communication Settings
  notificationPreferences?: {
    email?: {
      quotes?: boolean;
      applications?: boolean;
    };
    sms?: {
      quotes?: boolean;
      applications?: boolean;
    };
  };
  mobileDisplayConsent?: boolean;
  
  // UI Settings
  uiPreferences?: Record<string, unknown>;
  savedFilters?: Record<string, unknown>;
  dashboardFilters?: Record<string, unknown>;
  defaultViewMode?: string;
  
  // External Integration
  losUserId?: string;
  closingCostTemplateId?: string;
  encompassUserId?: string; // Synced from Encompass integration
  optimalBlueUserId?: string; // Linked from Optimal Blue
  optimalBlueUsername?: string;
  optimalBlueLinkedAt?: string;
  // OB compensation summary (cache populated by ob-comp-sync; see plan §E)
  optimalBlueBranchId?: string;
  optimalBlueBusinessChannelId?: string;
  optimalBlueCurrentScheduleId?: number;
  optimalBlueCurrentScheduleName?: string;
  optimalBlueCurrentPercentage?: number;
  optimalBlueCurrentMaxCap?: number;
  optimalBlueCurrentScheduleEffectiveDate?: string;
  optimalBlueCurrentScheduleScope?: 'core' | 'full_footprint' | 'custom_only' | 'mixed' | 'none';
  optimalBlueLastSyncedAt?: string;
  applicationUserId?: string;
  applicationLink?: string;
  applicationLinkGeneratedAt?: string;

  // Preferred Settlement Partners
  preferredTitleCompanyId?: string | null;
  preferredEscrowCompanyId?: string | null;

  // Twilio Integration
  businessSmsNumber?: string; // Twilio phone number assigned to this user
  twilioPhoneSid?: string; // Twilio Phone Number SID
  twilioNumberStatus?: string; // active, released, etc.
  twilioProvisionedAt?: string; // When number was provisioned
  twilioReleasedAt?: string; // When number was released
  smsForwardingEnabled?: boolean; // Enable SMS forwarding to mobile
  smsForwardingFormat?: 'simple' | 'rich'; // Format for forwarded messages
  voiceForwardingEnabled?: boolean; // Enable voice call forwarding
  voiceGreetingMessage?: string; // Custom greeting for incoming calls

  // ElevenLabs AI Voice Integration
  elevenlabsVoiceId?: string; // ElevenLabs voice ID
  voiceGreetingAudioUrl?: string; // URL to generated greeting audio
  voiceGreetingText?: string; // Text of the voice greeting
  voiceSource?: string; // 'preset' or 'custom'
  voiceConfiguredAt?: string; // When voice was first configured
  voiceLastGeneratedAt?: string; // When audio was last generated

  // Default Signature (for Wisr SMS pre-approval automation)
  defaultSignatureDataUrl?: string; // PNG data URL of saved signature
  defaultSignatureMethod?: 'drawn' | 'typed'; // How signature was created
  defaultSignatureFont?: string; // Font used for typed signature
  defaultSignatureSavedAt?: string; // When signature was last saved

  // System fields
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  deletedAt?: string;

  // Profile completeness
  profileComplete: boolean; // Generated field
}

/**
 * Employee-directory slice of a user.
 *
 * The Home roster is readable by every active employee, so it is served by the
 * `get_organization_roster` RPC rather than by selecting public.users, whose
 * compensation and Encompass columns must stay behind the existing RLS
 * policies. This type is that RPC's contract: widening it means widening the
 * SQL projection too. A full `User` remains assignable to it.
 */
export type RosterUser = Pick<
  User,
  | 'id'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'roles'
  | 'employmentStatus'
  | 'title'
  | 'phone'
  | 'mobilePhone'
  | 'branchName'
  | 'nmlsNumber'
  | 'businessSmsNumber'
  | 'twilioNumberStatus'
>;

/** Raw row shape returned by the `get_organization_roster` RPC. */
export interface RosterUserDB {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  title: string | null;
  phone: string | null;
  mobile_phone: string | null;
  business_sms_number: string | null;
  twilio_number_status: string | null;
  branch_name: string | null;
  nmls_number: string | null;
  roles: string[] | null;
  employment_status: string | null;
}

export function transformRosterUserFromDB(row: RosterUserDB): RosterUser {
  return {
    id: row.id,
    email: row.email,
    firstName: row.first_name || '',
    lastName: row.last_name || '',
    roles: row.roles || [],
    employmentStatus:
      (row.employment_status as 'pending' | 'active' | 'terminated') || 'pending',
    title: row.title ?? undefined,
    phone: row.phone ?? undefined,
    mobilePhone: row.mobile_phone ?? undefined,
    branchName: row.branch_name ?? undefined,
    nmlsNumber: row.nmls_number ?? undefined,
    businessSmsNumber: row.business_sms_number ?? undefined,
    twilioNumberStatus: row.twilio_number_status ?? undefined
  };
}

/**
 * Database User type (snake_case)
 * Maps directly to database columns
 */
export interface UserDB {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  mobile_phone?: string;
  office_extension?: string;
  nmls_number?: string;
  state_license_number?: string;
  license_states?: string[];
  title?: string;
  years_of_experience?: number | null;
  mortgage_industry_start_year?: number | null;
  roles: string[];
  employment_status?: string; // 'active', 'pending', 'terminated'
  is_loan_officer: boolean;
  organization_id: string;
  organization?: {
    id: string;
    name: string;
  };
  branch_name?: string;
  branch_address?: User['branchAddress'];
  address_preference?: 'corporate' | 'custom';
  bio?: string;
  specialties?: string[];
  mortgage_specialties?: User['mortgageSpecialties'];
  profile_photo_facial?: string;
  profile_photo_body?: string;
  photo_crop_data?: User['photoCropData'];
  website?: string;
  website_template?: string;
  website_slug?: string;
  website_enabled?: boolean;
  website_settings?: User['websiteSettings'];
  profile_published_at?: string;
  profile_view_count?: number;
  linkedin_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  calendar_link?: string;
  current_compensation_plan?: number;
  compensation_locked_until?: string;
  commission_rate?: number;
  bonus_structure?: User['bonusStructure'];
  compensation_tier?: string;
  broker_commission_split?: number;
  mentor_id?: string;
  mentorship_active?: boolean;
  broker_mentor_commission_split?: number;
  brokered_deals_completed?: number;
  banked_compensation?: number;
  maximum_compensation?: number;
  banked_override_bps?: number;
  override_recipient_id?: string;
  notification_preferences?: User['notificationPreferences'];
  mobile_display_consent?: boolean;
  ui_preferences?: User['uiPreferences'];
  saved_filters?: User['savedFilters'];
  dashboard_filters?: User['dashboardFilters'];
  default_view_mode?: string;
  los_user_id?: string;
  closing_cost_template_id?: string;
  encompass_user_id?: string;
  optimal_blue_user_id?: string;
  optimal_blue_username?: string;
  optimal_blue_linked_at?: string;
  optimal_blue_branch_id?: string;
  optimal_blue_business_channel_id?: string;
  optimal_blue_current_schedule_id?: number;
  optimal_blue_current_schedule_name?: string;
  optimal_blue_current_percentage?: number;
  optimal_blue_current_max_cap?: number;
  optimal_blue_current_schedule_effective_date?: string;
  optimal_blue_current_schedule_scope?: string;
  optimal_blue_last_synced_at?: string | null;
  application_user_id?: string;
  application_link?: string;
  application_link_generated_at?: string;
  preferred_title_company_id?: string | null;
  preferred_escrow_company_id?: string | null;
  business_sms_number?: string;
  twilio_phone_sid?: string;
  twilio_number_status?: string;
  twilio_provisioned_at?: string;
  twilio_released_at?: string;
  sms_forwarding_enabled?: boolean;
  sms_forwarding_format?: string;
  voice_forwarding_enabled?: boolean;
  voice_greeting_message?: string;
  elevenlabs_voice_id?: string;
  voice_greeting_audio_url?: string;
  voice_greeting_text?: string;
  voice_source?: string;
  voice_configured_at?: string;
  voice_last_generated_at?: string;
  default_signature_data_url?: string;
  default_signature_method?: string;
  default_signature_font?: string;
  default_signature_saved_at?: string;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  deleted_at?: string;
  profile_complete: boolean;
}

/**
 * Transform database user to application user
 */
export function transformUserFromDB(dbUser: UserDB): User {
  return {
    id: dbUser.id,
    email: dbUser.email,
    firstName: dbUser.first_name || '',
    lastName: dbUser.last_name || '',
    fullName: dbUser.full_name || '',
    phone: dbUser.phone,
    mobilePhone: dbUser.mobile_phone,
    officeExtension: dbUser.office_extension,
    roles: dbUser.roles || [],
    // WARNING: Converting employment_status to boolean loses information
    // 'pending' → false, 'terminated' → false (indistinguishable)
    // Prefer using employmentStatus directly for business logic
    isActive: dbUser.employment_status === 'active', // Derived from employment_status
    employmentStatus: (dbUser.employment_status as 'pending' | 'active' | 'terminated') || 'pending',
    isLoanOfficer: dbUser.is_loan_officer,
    organizationId: dbUser.organization_id,
    organization: dbUser.organization,
    branchName: dbUser.branch_name,
    branchAddress: dbUser.branch_address,
    addressPreference: dbUser.address_preference,
    nmlsNumber: dbUser.nmls_number,
    stateLicenseNumber: dbUser.state_license_number,
    licensedStates: dbUser.license_states,
    title: dbUser.title,
    yearsOfExperience: dbUser.years_of_experience,
    mortgageIndustryStartYear: dbUser.mortgage_industry_start_year,
    bio: dbUser.bio,
    specialties: dbUser.specialties,
    mortgageSpecialties: dbUser.mortgage_specialties,
    profilePhotoFacial: dbUser.profile_photo_facial,
    profilePhotoBody: dbUser.profile_photo_body,
    photoCropData: dbUser.photo_crop_data,
    website: dbUser.website,
    websiteTemplate: dbUser.website_template,
    websiteSlug: dbUser.website_slug,
    websiteEnabled: dbUser.website_enabled,
    websiteSettings: dbUser.website_settings,
    profilePublishedAt: dbUser.profile_published_at,
    profileViewCount: dbUser.profile_view_count,
    linkedinUrl: dbUser.linkedin_url,
    facebookUrl: dbUser.facebook_url,
    instagramUrl: dbUser.instagram_url,
    tiktokUrl: dbUser.tiktok_url,
    calendarLink: dbUser.calendar_link,
    currentCompensationPlan: dbUser.current_compensation_plan,
    compensationLockedUntil: dbUser.compensation_locked_until,
    commissionRate: dbUser.commission_rate,
    bonusStructure: dbUser.bonus_structure,
    compensationTier: dbUser.compensation_tier,
    brokerCommissionSplit: dbUser.broker_commission_split,
    mentorId: dbUser.mentor_id,
    mentorshipActive: dbUser.mentorship_active,
    brokerMentorCommissionSplit: dbUser.broker_mentor_commission_split,
    brokeredDealsCompleted: dbUser.brokered_deals_completed,
    bankedCompensation: dbUser.banked_compensation,
    maximumCompensation: dbUser.maximum_compensation,
    bankedOverrideBps: dbUser.banked_override_bps,
    overrideRecipientId: dbUser.override_recipient_id,
    notificationPreferences: dbUser.notification_preferences,
    mobileDisplayConsent: dbUser.mobile_display_consent,
    uiPreferences: dbUser.ui_preferences,
    savedFilters: dbUser.saved_filters,
    dashboardFilters: dbUser.dashboard_filters,
    defaultViewMode: dbUser.default_view_mode,
    losUserId: dbUser.los_user_id,
    closingCostTemplateId: dbUser.closing_cost_template_id,
    encompassUserId: dbUser.encompass_user_id,
    optimalBlueUserId: dbUser.optimal_blue_user_id,
    optimalBlueUsername: dbUser.optimal_blue_username,
    optimalBlueLinkedAt: dbUser.optimal_blue_linked_at,
    optimalBlueBranchId: dbUser.optimal_blue_branch_id,
    optimalBlueBusinessChannelId: dbUser.optimal_blue_business_channel_id,
    optimalBlueCurrentScheduleId: dbUser.optimal_blue_current_schedule_id,
    optimalBlueCurrentScheduleName: dbUser.optimal_blue_current_schedule_name,
    optimalBlueCurrentPercentage: dbUser.optimal_blue_current_percentage,
    optimalBlueCurrentMaxCap: dbUser.optimal_blue_current_max_cap,
    optimalBlueCurrentScheduleEffectiveDate: dbUser.optimal_blue_current_schedule_effective_date,
    optimalBlueCurrentScheduleScope: dbUser.optimal_blue_current_schedule_scope as User['optimalBlueCurrentScheduleScope'],
    optimalBlueLastSyncedAt: dbUser.optimal_blue_last_synced_at ?? undefined,
    applicationUserId: dbUser.application_user_id,
    applicationLink: dbUser.application_link,
    applicationLinkGeneratedAt: dbUser.application_link_generated_at,
    preferredTitleCompanyId: dbUser.preferred_title_company_id ?? null,
    preferredEscrowCompanyId: dbUser.preferred_escrow_company_id ?? null,
    businessSmsNumber: dbUser.business_sms_number,
    twilioPhoneSid: dbUser.twilio_phone_sid,
    twilioNumberStatus: dbUser.twilio_number_status,
    twilioProvisionedAt: dbUser.twilio_provisioned_at,
    twilioReleasedAt: dbUser.twilio_released_at,
    smsForwardingEnabled: dbUser.sms_forwarding_enabled,
    smsForwardingFormat: dbUser.sms_forwarding_format as 'simple' | 'rich' | undefined,
    voiceForwardingEnabled: dbUser.voice_forwarding_enabled,
    voiceGreetingMessage: dbUser.voice_greeting_message,
    elevenlabsVoiceId: dbUser.elevenlabs_voice_id,
    voiceGreetingAudioUrl: dbUser.voice_greeting_audio_url,
    voiceGreetingText: dbUser.voice_greeting_text,
    voiceSource: dbUser.voice_source,
    voiceConfiguredAt: dbUser.voice_configured_at,
    voiceLastGeneratedAt: dbUser.voice_last_generated_at,
    defaultSignatureDataUrl: dbUser.default_signature_data_url,
    defaultSignatureMethod: dbUser.default_signature_method as 'drawn' | 'typed' | undefined,
    defaultSignatureFont: dbUser.default_signature_font,
    defaultSignatureSavedAt: dbUser.default_signature_saved_at,
    createdAt: dbUser.created_at,
    updatedAt: dbUser.updated_at,
    lastLoginAt: dbUser.last_login_at,
    deletedAt: dbUser.deleted_at,
    profileComplete: dbUser.profile_complete
  };
}

/**
 * Transform application user to database format
 */
export function transformUserToDB(user: Partial<User>): Partial<UserDB> {
  const dbUser: Partial<UserDB> = {};
  
  // Validation: Warn about snake_case fields in input (they should be camelCase)
  if (process.env.NODE_ENV === 'development') {
    Object.keys(user).forEach(key => {
      if (key.includes('_')) {
        console.warn(
          `⚠️ Field "${key}" uses snake_case but should be camelCase.`,
          `The transform expects camelCase fields.`
        );
      }
    });
  }
  
  if (user.id !== undefined) {
    dbUser.id = user.id;
  }
  if (user.email !== undefined) {
    dbUser.email = user.email;
  }
  if (user.firstName !== undefined) {
    dbUser.first_name = user.firstName;
  }
  if (user.lastName !== undefined) {
    dbUser.last_name = user.lastName;
  }
  if (user.phone !== undefined) {
    dbUser.phone = user.phone;
  }
  if (user.mobilePhone !== undefined) {
    dbUser.mobile_phone = user.mobilePhone;
  }
  if (user.officeExtension !== undefined) {
    dbUser.office_extension = user.officeExtension;
  }
  if (user.roles !== undefined) {
    dbUser.roles = user.roles;
  }
  // CRITICAL: Only write valid status values: 'pending', 'active', 'terminated'
  // Database constraint prevents 'inactive' - this would cause runtime error
  if (user.isActive !== undefined) {
    dbUser.employment_status = user.isActive ? 'active' : 'terminated';
  }
  if (user.organizationId !== undefined) {
    dbUser.organization_id = user.organizationId;
  }
  if (user.branchName !== undefined) {
    dbUser.branch_name = user.branchName;
  }
  if (user.branchAddress !== undefined) {
    dbUser.branch_address = user.branchAddress;
  }
  if (user.addressPreference !== undefined) {
    dbUser.address_preference = user.addressPreference;
  }
  if (user.nmlsNumber !== undefined) {
    dbUser.nmls_number = user.nmlsNumber;
  }
  if (user.stateLicenseNumber !== undefined) {
    dbUser.state_license_number = user.stateLicenseNumber;
  }
  if (user.licensedStates !== undefined) {
    dbUser.license_states = user.licensedStates;
  }
  if (user.title !== undefined) {
    dbUser.title = user.title;
  }
  if (user.yearsOfExperience !== undefined) {
    dbUser.years_of_experience = user.yearsOfExperience;
  }
  if (user.mortgageIndustryStartYear !== undefined) {
    dbUser.mortgage_industry_start_year = user.mortgageIndustryStartYear;
  }
  if (user.bio !== undefined) {
    dbUser.bio = user.bio;
  }
  if (user.specialties !== undefined) {
    dbUser.specialties = user.specialties;
  }
  if (user.mortgageSpecialties !== undefined) {
    dbUser.mortgage_specialties = user.mortgageSpecialties;
  }
  // Photo fields
  if (user.profilePhotoFacial !== undefined) {
    dbUser.profile_photo_facial = user.profilePhotoFacial;
  }
  if (user.profilePhotoBody !== undefined) {
    dbUser.profile_photo_body = user.profilePhotoBody;
  }
  if (user.website !== undefined) {
    dbUser.website = user.website;
  }
  if (user.websiteTemplate !== undefined) {
    dbUser.website_template = user.websiteTemplate;
  }
  if (user.websiteSlug !== undefined) {
    dbUser.website_slug = user.websiteSlug;
  }
  if (user.websiteEnabled !== undefined) {
    dbUser.website_enabled = user.websiteEnabled;
  }
  if (user.linkedinUrl !== undefined) {
    dbUser.linkedin_url = user.linkedinUrl;
  }
  if (user.facebookUrl !== undefined) {
    dbUser.facebook_url = user.facebookUrl;
  }
  if (user.instagramUrl !== undefined) {
    dbUser.instagram_url = user.instagramUrl;
  }
  if (user.tiktokUrl !== undefined) {
    dbUser.tiktok_url = user.tiktokUrl;
  }
  if (user.calendarLink !== undefined) {
    dbUser.calendar_link = user.calendarLink;
  }
  if (user.currentCompensationPlan !== undefined) {
    dbUser.current_compensation_plan = user.currentCompensationPlan;
  }
  if (user.compensationLockedUntil !== undefined) {
    dbUser.compensation_locked_until = user.compensationLockedUntil;
  }
  if (user.commissionRate !== undefined) {
    dbUser.commission_rate = user.commissionRate;
  }
  if (user.notificationPreferences !== undefined) {
    dbUser.notification_preferences = user.notificationPreferences;
  }
  if (user.mobileDisplayConsent !== undefined) {
    dbUser.mobile_display_consent = user.mobileDisplayConsent;
  }
  if (user.uiPreferences !== undefined) {
    dbUser.ui_preferences = user.uiPreferences;
  }
  if (user.savedFilters !== undefined) {
    dbUser.saved_filters = user.savedFilters;
  }
  if (user.dashboardFilters !== undefined) {
    dbUser.dashboard_filters = user.dashboardFilters;
  }
  if (user.defaultViewMode !== undefined) {
    dbUser.default_view_mode = user.defaultViewMode;
  }
  if (user.losUserId !== undefined) {
    dbUser.los_user_id = user.losUserId;
  }
  if (user.closingCostTemplateId !== undefined) {
    dbUser.closing_cost_template_id = user.closingCostTemplateId;
  }
  if (user.encompassUserId !== undefined) {
    dbUser.encompass_user_id = user.encompassUserId;
  }
  if (user.optimalBlueUserId !== undefined) {
    dbUser.optimal_blue_user_id = user.optimalBlueUserId;
  }
  if (user.optimalBlueUsername !== undefined) {
    dbUser.optimal_blue_username = user.optimalBlueUsername;
  }
  if (user.optimalBlueLinkedAt !== undefined) {
    dbUser.optimal_blue_linked_at = user.optimalBlueLinkedAt;
  }
  if (user.applicationUserId !== undefined) {
    dbUser.application_user_id = user.applicationUserId;
  }
  if (user.applicationLink !== undefined) {
    dbUser.application_link = user.applicationLink;
  }
  if (user.applicationLinkGeneratedAt !== undefined) {
    dbUser.application_link_generated_at = user.applicationLinkGeneratedAt;
  }
  if (user.preferredTitleCompanyId !== undefined) {
    dbUser.preferred_title_company_id = user.preferredTitleCompanyId;
  }
  if (user.preferredEscrowCompanyId !== undefined) {
    dbUser.preferred_escrow_company_id = user.preferredEscrowCompanyId;
  }
  if (user.businessSmsNumber !== undefined) {
    dbUser.business_sms_number = user.businessSmsNumber;
  }
  if (user.twilioPhoneSid !== undefined) {
    dbUser.twilio_phone_sid = user.twilioPhoneSid;
  }
  if (user.twilioNumberStatus !== undefined) {
    dbUser.twilio_number_status = user.twilioNumberStatus;
  }
  if (user.twilioProvisionedAt !== undefined) {
    dbUser.twilio_provisioned_at = user.twilioProvisionedAt;
  }
  if (user.twilioReleasedAt !== undefined) {
    dbUser.twilio_released_at = user.twilioReleasedAt;
  }
  if (user.smsForwardingEnabled !== undefined) {
    dbUser.sms_forwarding_enabled = user.smsForwardingEnabled;
  }
  if (user.smsForwardingFormat !== undefined) {
    dbUser.sms_forwarding_format = user.smsForwardingFormat;
  }
  if (user.voiceForwardingEnabled !== undefined) {
    dbUser.voice_forwarding_enabled = user.voiceForwardingEnabled;
  }
  if (user.voiceGreetingMessage !== undefined) {
    dbUser.voice_greeting_message = user.voiceGreetingMessage;
  }
  if (user.elevenlabsVoiceId !== undefined) {
    dbUser.elevenlabs_voice_id = user.elevenlabsVoiceId;
  }
  if (user.voiceGreetingAudioUrl !== undefined) {
    dbUser.voice_greeting_audio_url = user.voiceGreetingAudioUrl;
  }
  if (user.voiceGreetingText !== undefined) {
    dbUser.voice_greeting_text = user.voiceGreetingText;
  }
  if (user.voiceSource !== undefined) {
    dbUser.voice_source = user.voiceSource;
  }
  if (user.voiceConfiguredAt !== undefined) {
    dbUser.voice_configured_at = user.voiceConfiguredAt;
  }
  if (user.voiceLastGeneratedAt !== undefined) {
    dbUser.voice_last_generated_at = user.voiceLastGeneratedAt;
  }
  if (user.defaultSignatureDataUrl !== undefined) {
    dbUser.default_signature_data_url = user.defaultSignatureDataUrl;
  }
  if (user.defaultSignatureMethod !== undefined) {
    dbUser.default_signature_method = user.defaultSignatureMethod;
  }
  if (user.defaultSignatureFont !== undefined) {
    dbUser.default_signature_font = user.defaultSignatureFont;
  }
  if (user.defaultSignatureSavedAt !== undefined) {
    dbUser.default_signature_saved_at = user.defaultSignatureSavedAt;
  }

  return dbUser;
}

/**
 * User role types
 */
export type UserRole =
  | 'admin'
  | 'loan_officer'
  | 'loan_officer_assistant'
  | 'processor'
  | 'underwriter'
  | 'manager'
  | 'closer'
  | 'compliance_specialist'
  | 'partner'
  | 'recruiter'
  | 'user';

/**
 * Check if user has a specific role
 */
export function hasRole(user: User, role: UserRole): boolean {
  return user.roles.includes(role);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(user: User, roles: UserRole[]): boolean {
  return roles.some(role => user.roles.includes(role));
}

/**
 * Check if user has all of the specified roles
 */
export function hasAllRoles(user: User, roles: UserRole[]): boolean {
  return roles.every(role => user.roles.includes(role));
}

/**
 * Helper function to check if employment status indicates an active user
 */
export function isEmploymentActive(employmentStatus?: string): boolean {
  return employmentStatus === 'active';
}
